import { MaybeRef, unref } from 'vue';
import { saveAs } from 'file-saver';
import type { View } from '@/src/core/vtk/types';
import type { Maybe } from '@/src/types';

/**
 * Registry of live vtk views so the toolbar's screenshot action can capture
 * every visible viewport. Views self-register via useVtkView.
 */
type RegisteredView = {
  view: View;
  container: MaybeRef<Maybe<HTMLElement>>;
};

const registeredViews = new Set<RegisteredView>();

export function registerViewForScreenshot(
  view: View,
  container: MaybeRef<Maybe<HTMLElement>>
) {
  const entry: RegisteredView = { view, container };
  registeredViews.add(entry);
  return () => {
    registeredViews.delete(entry);
  };
}

function isVisible(el: HTMLElement) {
  if (!el.isConnected) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 1 && rect.height > 1 && el.offsetParent !== null;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load captured image'));
    img.src = src;
  });
}

/**
 * Rasterize an inline SVG overlay (annotation drawings) so it can be
 * composited on top of the WebGL capture.
 */
async function rasterizeSvg(
  svg: SVGSVGElement,
  width: number,
  height: number
): Promise<HTMLImageElement | null> {
  if (width < 1 || height < 1) return null;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', `${width}`);
  clone.setAttribute('height', `${height}`);
  const markup = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    return await loadImage(url);
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function captureView(entry: RegisteredView): Promise<string | null> {
  const { view } = entry;
  try {
    const promises = view.renderWindowView.captureNextImage('image/png', {});
    // captureNextImage resolves after the next render traversal
    view.renderWindow.render();
    const image = await promises;
    return image ?? null;
  } catch {
    return null;
  }
}

/**
 * Capture all visible viewports (image + SVG annotation overlays) into a
 * single PNG laid out as on screen, and trigger a download.
 */
export async function captureViewportScreenshot(filename?: string) {
  const entries = [...registeredViews].filter((entry) => {
    const el = unref(entry.container);
    return el && isVisible(el);
  });

  if (entries.length === 0) {
    throw new Error('No visible views to capture');
  }

  // union of all view rects (CSS pixels)
  const rects = entries.map((entry) => {
    const el = unref(entry.container)!;
    return { entry, el, rect: el.getBoundingClientRect() };
  });
  const left = Math.min(...rects.map(({ rect }) => rect.left));
  const top = Math.min(...rects.map(({ rect }) => rect.top));
  const right = Math.max(...rects.map(({ rect }) => rect.right));
  const bottom = Math.max(...rects.map(({ rect }) => rect.bottom));

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(right - left);
  canvas.height = Math.round(bottom - top);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2D canvas context');

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  await Promise.all(
    rects.map(async ({ entry, el, rect }) => {
      const x = Math.round(rect.left - left);
      const y = Math.round(rect.top - top);
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);

      const dataUrl = await captureView(entry);
      if (dataUrl) {
        const img = await loadImage(dataUrl);
        ctx.drawImage(img, x, y, w, h);
      }

      // annotation overlays live in sibling SVGs within the same view stage
      const stage = el.closest('.pycad-vtk-stage') ?? el.parentElement;
      if (!stage) return;
      const svgs = [
        ...stage.querySelectorAll<SVGSVGElement>('svg.overlay-no-events'),
      ];
      const overlays = await Promise.all(
        svgs.map(async (svg) => {
          const svgRect = svg.getBoundingClientRect();
          const overlay = await rasterizeSvg(
            svg,
            svgRect.width,
            svgRect.height
          );
          return { overlay, svgRect };
        })
      );
      overlays.forEach(({ overlay, svgRect }) => {
        if (overlay) {
          ctx.drawImage(
            overlay,
            Math.round(svgRect.left - left),
            Math.round(svgRect.top - top)
          );
        }
      });
    })
  );

  const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
  const name = filename ?? `smilesuite-screenshot-${stamp}.png`;

  await new Promise<void>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('PNG encoding failed'));
        return;
      }
      saveAs(blob, name);
      resolve();
    }, 'image/png');
  });
}
